<script>
import { iButton, iTextInput, showToast } from '@inscribe/signal';
import { isValidPassword } from '@/utils/validation';
import PasswordInput from '@/V2/auth/PasswordInput';

export default {
  components: {
    iButton,
    iTextInput,
    'password-input': PasswordInput,
  },
  props: {
    invitationCode: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      loading: false,
      firstName: '',
      lastName: '',
      password: '',
      passwordConfirmation: '',
      showPassword: false,
      displayHint: false,
    };
  },
  computed: {
    isValidPassword() {
      return isValidPassword(this.password);
    },
    doPasswordsMatch() {
      return this.password === this.passwordConfirmation;
    },
    isSamlStrictModeEnabled() {
      return this.$route.query.samlStrictMode === 'True';
    },
  },
  methods: {
    joinOrganisation() {
      this.displayHint = false;
      if (
        this.firstName &&
        this.lastName &&
        ((this.isValidPassword && this.doPasswordsMatch) ||
          this.isSamlStrictModeEnabled) &&
        !this.loading
      ) {
        this.loading = true;
        const data = new FormData();
        data.append('first_name', this.firstName);
        data.append('last_name', this.lastName);
        data.append('password', this.password);
        data.append('password_confirmation', this.passwordConfirmation);
        data.append('invitation_code', this.invitationCode);
        this.$store
          .dispatch('joinOrganisation', data)
          .then(() => {
            this.loading = false;
            if (this.isSamlStrictModeEnabled) {
              this.$router.push({ name: 'login-saml' });
            } else {
              this.$router.push({ name: 'login' });
            }
          })
          .catch((error) => {
            this.loading = false;
            showToast({
              title: 'There was a problem joining your team',
              text: error?.response?.data?.message,
              isError: true,
            });
          });
      } else {
        this.displayHint = true;
      }
    },
  },
};
</script>

<template>
  <div
    class="join-organisation-screen auth-screen d-flex flex-column align-items-center justify-content-center i-gap-10"
  >
    <div class="title d-flex flex-column align-items-center i-gap-1">
      <div class="i-text-heading-3 i-text-neutral-120">Join Team</div>
      <div class="i-text-body-1 i-text-neutral-110">
        <span v-if="!isSamlStrictModeEnabled">
          Add your name and password to join
        </span>
        <span v-else> Add your name to join </span>
      </div>
    </div>

    <div class="form d-flex flex-column i-gap-10">
      <i-text-input
        label="First name"
        :value="firstName"
        placeholder="Your name"
        name="first-name"
        type="text"
        :has-error="displayHint && !firstName"
        :show-hint-text="displayHint && !firstName"
        :max-length="30"
        hint-text="Please enter your name"
        data-testid="join-firstname"
        @input="firstName = $event"
      ></i-text-input>

      <i-text-input
        label="Last name"
        :value="lastName"
        placeholder="Your last name"
        name="last-name"
        type="text"
        :max-length="30"
        :has-error="displayHint && !lastName"
        :show-hint-text="displayHint && !lastName"
        hint-text="Please enter your last name"
        data-testid="join-lastname"
        @input="lastName = $event"
      ></i-text-input>

      <password-input
        v-if="!isSamlStrictModeEnabled"
        :value="password"
        :display-hint="displayHint"
        label="Password"
        @input="password = $event"
      />
      <i-text-input
        v-if="!isSamlStrictModeEnabled"
        label="Confirm password"
        :value="passwordConfirmation"
        placeholder="One more time"
        name="confirm-password"
        :type="showPassword ? 'text' : 'password'"
        :has-error="displayHint && !doPasswordsMatch"
        :show-hint-text="displayHint && !doPasswordsMatch"
        hint-text="Your passwords don't match"
        :right-icon="showPassword ? 'small-edit-view-off' : 'small-edit-view'"
        data-testid="join-password-confirm"
        @input="passwordConfirmation = $event"
      >
      </i-text-input>

      <!-- join button -->
      <i-button
        text="Join Team"
        type="primary"
        subtype="strong"
        data-testid="join-organisation-button"
        size="lg"
        :loading="loading"
        @button-click="joinOrganisation"
      ></i-button>
    </div>
  </div>
</template>
